import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import VoiceSynthesizer from './VoiceSynthesizer';
import PoseLandmarkerInitializer from './PoseLandmarkerInitializer';
import CameraAccessChecker from './CameraAccessChecker';
import PoseCaptureHandler from './PoseCaptureHandler';
import CountdownManager from './CountdownManager';
import WebcamFeed from './WebcamFeed';
import FeedbackPanel from './FeedbackPanel';
import InstructionsModal from './InstructionsModal';
import HeaderBar from './HeaderBar';
import Webcam from 'react-webcam';
import toast from 'react-hot-toast';
import { PoseCaptureProvider } from 'contexts/PoseCaptureContext';
import ErrorBoundary from './ErrorBoundary';

// ─── Recording Banner ─────────────────────────────────────────────────────
function RecordingBanner({ isRecording, recordingDuration, maxRecordingSeconds, onStop }) {
  if (!isRecording) return null;

  const progress = ((maxRecordingSeconds - recordingDuration) / maxRecordingSeconds) * 100;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1300,
        bgcolor: 'rgba(20, 20, 20, 0.94)',
        backdropFilter: 'blur(10px)',
        border: '2px solid rgba(239, 68, 68, 0.8)',
        borderRadius: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        minWidth: 360,
        boxShadow: '0 0 32px rgba(239, 68, 68, 0.4)'
      }}
    >
      {/* Pulsing red dot */}
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          bgcolor: '#ef4444',
          flexShrink: 0,
          animation: 'pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.5, transform: 'scale(0.9)' }
          }
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#f87171',
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              fontSize: '0.75rem'
            }}
          >
            🎬 Recording Your Asana
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#fca5a5',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              fontSize: '0.85rem'
            }}
          >
            {recordingDuration}s
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 5,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'linear-gradient(90deg, #ef4444, #dc2626)',
              borderRadius: 3
            }
          }}
        />
      </Box>

      <Button
        size="small"
        onClick={onStop}
        sx={{
          bgcolor: '#ef4444',
          color: 'white',
          borderRadius: 2,
          px: 2,
          py: 0.75,
          fontSize: '0.75rem',
          fontWeight: 700,
          flexShrink: 0,
          textTransform: 'uppercase',
          '&:hover': { bgcolor: '#dc2626' },
          transition: 'all 0.2s'
        }}
      >
        Stop
      </Button>
    </Box>
  );
}

// ─── Auto-Detection Alert ────────────────────────────────────────────────
function AutoDetectionAlert({ isFullBodyVisible }) {
  if (!isFullBodyVisible) {
    return (
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <AlertTitle>📸 Position Setup</AlertTitle>
        Move closer or adjust position so your full body (head to feet) is visible in the camera frame.
      </Alert>
    );
  }

  return (
    <Alert severity="success" sx={{ mb: 2, borderRadius: 2, animation: 'slideDown 0.3s ease-out' }}>
      <AlertTitle>✅ Full Body Detected!</AlertTitle>
      Recording will start automatically in 3 seconds. Hold your starting pose...
    </Alert>
  );
}

// ─── Main Container ──────────────────────────────────────────────────────
function PoseInstructorContainer() {
  const [isTracking, setIsTracking] = useState(false);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullBodyVisible, setIsFullBodyVisible] = useState(false);
  const [poseType, setPoseType] = useState('unknown');
  const [missingPoints, setMissingPoints] = useState([]);
  const [lastMissingPoints, setLastMissingPoints] = useState([]);
  const [isTooClose, setIsTooClose] = useState(false);
  const [distance, setDistance] = useState(null);
  const [distanceStatus, setDistanceStatus] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [initialCountdown, setInitialCountdown] = useState(null);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [checkingVisibility, setCheckingVisibility] = useState(true);
  const [initialLandmarks, setInitialLandmarks] = useState(null);
  const [poseChanged, setPoseChanged] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [poseCaptureComplete, setPoseCaptureComplete] = useState(false);
  const [isPoseDetected, setIsPoseDetected] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [webcamReady, setWebcamReady] = useState(false);
  const [isPoseLandmarksReady, setIsPoseLandmarksReady] = useState(false);
  const [latestLandmarks, setLatestLandmarks] = useState(null);
  const [finalLandmarks, setFinalLandmarks] = useState(null);
  const [instructionsAccepted, setInstructionsAccepted] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const MAX_RECORDING_SECONDS = 15;
  const onStopRecordingRef = useRef(null);

  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const poseLandmarkerRef = useRef(null);
  const canvasRef = useRef(null);
  const startCountdownRef = useRef(() => {
    console.log('Default startCountdownRef called');
  });

  const location = useLocation();
  const { posture } = location.state || {};

  // ─── Setup Effects ───────────────────────────────────────────────────────
  useEffect(() => {
    const video = webcamRef.current?.video;
    if (video) {
      const handleCanPlay = () => {
        console.log('✅ Webcam ready');
        setWebcamReady(true);
      };
      video.addEventListener('canplay', handleCanPlay);
      return () => video.removeEventListener('canplay', handleCanPlay);
    }
  }, []);

  useEffect(() => {
    if (!posture || !posture.postureId) {
      toast.error('Invalid or missing posture data');
      navigate('/instructor');
    }
  }, [posture, navigate]);

  useEffect(() => {
    if (error) {
      console.error('Error:', error);
      toast.error(error);
    }
  }, [error]);

  const lastResetRef = useRef(0);
  const handlePoseChangeDuringFinalCountdown = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current > 2000) {
      setCountdown(3);
      if (latestLandmarks) setFinalLandmarks(latestLandmarks);
      lastResetRef.current = now;
    }
  }, [latestLandmarks]);

  const handleVisibilityChange = useCallback(
    ({ fullBodyVisible, poseType, missingPoints = [], isTooClose, distance, distanceStatus, error, landmarks }) => {
      if (error) {
        setError(error);
        setIsFullBodyVisible(false);
        setIsCountdownActive(false);
        setInitialCountdown(null);
        return;
      }
      setIsFullBodyVisible(fullBodyVisible);
      setPoseType(poseType);
      setMissingPoints(missingPoints);
      setLastMissingPoints(missingPoints);
      setIsTooClose(isTooClose);
      setDistance(distance);
      setDistanceStatus(distanceStatus);
      setIsPoseDetected(landmarks?.length > 0);
      setLatestLandmarks(landmarks);

      if (isCountdownActive && countdown !== null && finalLandmarks && landmarks?.length) {
        const deviation =
          landmarks.reduce((sum, point, i) => {
            if (!finalLandmarks[i]) return sum;
            const dx = point.x - finalLandmarks[i].x;
            const dy = point.y - finalLandmarks[i].y;
            return sum + Math.sqrt(dx * dx + dy * dy);
          }, 0) / landmarks.length;
        setPoseChanged(countdown <= 3 && countdown >= 1 && deviation > 0.03);
      }
    },
    [isCountdownActive, countdown, finalLandmarks]
  );

  const handleStopRecording = () => {
    if (onStopRecordingRef.current) {
      onStopRecordingRef.current();
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
      <PoseCaptureProvider>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: { xs: 2, md: 4 },
            bgcolor: '#f5f5f5',
            minHeight: '100vh'
          }}
        >
          {instructionsAccepted && (
            <>
              {/* Recording Banner (fixed top) */}
              <RecordingBanner
                isRecording={isRecording}
                recordingDuration={recordingDuration}
                maxRecordingSeconds={MAX_RECORDING_SECONDS}
                onStop={handleStopRecording}
              />

              {/* Webcam (hidden) */}
              <Webcam
                ref={webcamRef}
                audio={false}
                muted
                videoConstraints={{ facingMode: 'user' }}
                onUserMedia={() => {
                  console.log('✅ Webcam stream active');
                  setWebcamReady(true);
                }}
                onUserMediaError={(err) => {
                  console.error('Webcam error:', err);
                  toast.error('Failed to access webcam');
                }}
                style={{ display: 'none' }}
              />

              <HeaderBar posture={posture} />

              {/* Auto-Detection Alert */}
              {!isRecording && webcamReady && isPoseLandmarksReady && (
                <AutoDetectionAlert isFullBodyVisible={isFullBodyVisible} />
              )}

              {/* Recording Status Chip */}
              {isRecording && (
                <Chip
                  label={`🔴 Recording: ${posture?.posturename || 'Pose'} — ${recordingDuration}s remaining`}
                  sx={{
                    mb: 2,
                    bgcolor: '#fef2f2',
                    color: '#dc2626',
                    fontWeight: 600,
                    border: '1px solid #fecaca',
                    fontSize: '0.95rem'
                  }}
                />
              )}

              <VoiceSynthesizer
                speakDependencies={[
                  lastMissingPoints,
                  isTooClose,
                  distanceStatus,
                  poseChanged,
                  countdown,
                  initialCountdown,
                  posture?.name,
                  poseCaptureComplete,
                  poseType,
                  isRecording
                ]}
                isCountdownActive={isCountdownActive}
                checkingVisibility={checkingVisibility}
                isFullBodyVisible={isFullBodyVisible}
                missingPoints={missingPoints}
                lastMissingPoints={lastMissingPoints}
                isTooClose={isTooClose}
                distanceStatus={distanceStatus}
                poseChanged={poseChanged}
                countdown={countdown}
                initialCountdown={initialCountdown}
                postureName={posture?.name}
                isPoseDetected={isPoseDetected}
                attempts={attempts}
                poseCaptureComplete={poseCaptureComplete}
                poseType={poseType}
                onPoseChangeDuringFinalCountdown={handlePoseChangeDuringFinalCountdown}
                startCountdown={startCountdownRef}
              />

              <PoseLandmarkerInitializer
                setIsModelLoading={setIsModelLoading}
                setError={setError}
                poseLandmarkerRef={poseLandmarkerRef}
                setIsPoseLandmarksReady={setIsPoseLandmarksReady}
              />

              <CameraAccessChecker setError={setError} setIsModelLoading={setIsModelLoading} />

              {/* Auto-Start Handler */}
              <PoseCaptureHandler
                poseLandmarkerRef={poseLandmarkerRef}
                webcamRef={webcamRef}
                posture={posture}
                poseChanged={poseChanged}
                setPoseCaptureComplete={setPoseCaptureComplete}
                setCheckingVisibility={setCheckingVisibility}
                setAttempts={setAttempts}
                isPoseLandmarkerReady={isPoseLandmarksReady}
                isFullBodyVisible={isFullBodyVisible}           // ← Triggers auto-start
                setIsRecording={setIsRecording}
                setRecordingDuration={setRecordingDuration}
                onStopRecordingRef={onStopRecordingRef}
                maxRecordingSeconds={MAX_RECORDING_SECONDS}
              />

              {webcamReady && isPoseLandmarksReady && (
                <CountdownManager
                  isModelLoading={isModelLoading}
                  checkingVisibility={checkingVisibility}
                  isCountdownActive={isCountdownActive}
                  poseCaptureComplete={poseCaptureComplete}
                  isFullBodyVisible={isFullBodyVisible}
                  isTooClose={isTooClose}
                  distanceStatus={distanceStatus}
                  countdown={countdown}
                  setCountdown={setCountdown}
                  setIsCountdownActive={setIsCountdownActive}
                  setIsTracking={setIsTracking}
                  setMissingPoints={setMissingPoints}
                  setInitialLandmarks={setInitialLandmarks}
                  webcamRef={webcamRef}
                  poseLandmarkerRef={poseLandmarkerRef}
                  postureName={posture?.name}
                  initialCountdown={initialCountdown}
                  setInitialCountdown={setInitialCountdown}
                  missingPoints={missingPoints}
                  setFinalLandmarks={setFinalLandmarks}
                  startCountdown={startCountdownRef}
                />
              )}

              {/* Main Content */}
              {webcamReady ? (
                isModelLoading ? (
                  <Box
                    sx={{
                      maxWidth: 600,
                      width: '100%',
                      height: 100,
                      borderRadius: 3,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      bgcolor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Loading pose detection model…
                    </Typography>
                  </Box>
                ) : error ? (
                  <Box
                    sx={{
                      maxWidth: 600,
                      width: '100%',
                      borderRadius: 3,
                      bgcolor: 'white',
                      p: 3
                    }}
                  >
                    <Typography variant="body1" color="error" sx={{ fontWeight: 500 }}>
                      ⚠️ {error}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative', maxWidth: 1200, width: '100%' }}>
                    <Box
                      sx={{
                        borderRadius: 3,
                        outline: isRecording ? '3px solid #ef4444' : 'none',
                        boxShadow: isRecording ? '0 0 0 6px rgba(239,68,68,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s'
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <WebcamFeed
                            webcamRef={webcamRef}
                            poseLandmarkerRef={poseLandmarkerRef}
                            canvasRef={canvasRef}
                            isFullBodyVisible={isFullBodyVisible}
                            handleVisibilityChange={handleVisibilityChange}
                            poseType={poseType}
                            isActive={checkingVisibility}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FeedbackPanel
                            isFullBodyVisible={isFullBodyVisible}
                            isTooClose={isTooClose}
                            distance={distance}
                            distanceStatus={distanceStatus}
                            countdown={countdown}
                            initialCountdown={initialCountdown}
                            posture={posture}
                            missingPoints={missingPoints}
                            poseChanged={poseChanged}
                            attempts={attempts}
                            setAttempts={setAttempts}
                            poseType={poseType}
                            isRecording={isRecording}
                            recordingDuration={recordingDuration}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                )
              ) : (
                <Box
                  sx={{
                    maxWidth: 600,
                    width: '100%',
                    height: 100,
                    borderRadius: 3,
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Initializing webcam…
                  </Typography>
                </Box>
              )}
            </>
          )}

          <InstructionsModal
            showInstructions={showInstructions}
            setShowInstructions={setShowInstructions}
            onAccept={() => {
              setInstructionsAccepted(true);
              setShowInstructions(false);
            }}
          />
        </Box>
      </PoseCaptureProvider>
  );
}

export default PoseInstructorContainer;